// client/make-router.ts
import { assertExactlyOne } from "@adornis/assert/assert-exactly-one.js";
import { logger } from "@adornis/base/logging.js";
import { baseConfig } from "@adornis/config/baseConfig.js";
import { computed, Signal } from "@lit-labs/preact-signals";
import {
  generateFullRoutingStateFromInput,
  generateHrefFromRoutingState,
  joinPathParts,
  routingStateSignal,
  setRoutingState
} from "./routing-state-signal.js";
import "./click-handler.js";
import "urlpattern-polyfill";
function fillPathWithVariables(pathPattern, variables) {
  return pathPattern.replace(/\{([^}]+)\}/g, "").replace(/:(\w+)/g, (_, key) => variables[key] ?? "").replaceAll(/[\*\?]/g, "");
}
var IS_ROOT_ROUTER = Symbol("isRootRouter");
function makeRouter(routes, config) {
  const subRouters = Object.values(routes).map((route) => route.subRouter).filter(Boolean);
  if (new Set(subRouters).size !== subRouters.length)
    throw new Error("You mustn't use the same sub router multiple times in the same router");
  let myParent = new Signal(null);
  const routingStateSource = config?.routingStateSource ?? routingStateSignal;
  const routesArray = Object.entries(routes).map(([name, route]) => ({
    ...route,
    name
  }));
  const localPath = computed(() => {
    if (myParent.value === IS_ROOT_ROUTER)
      return routingStateSource.value.path;
    if (!myParent.value)
      throw new Error("router not initialized: " + config?.routerName);
    return myParent.value.subroute.value;
  });
  const currentRoute = computed(() => {
    if (!myParent.value)
      throw new Error("router not initialized: " + config?.routerName);
    const parentPath = myParent.value === IS_ROOT_ROUTER ? "/" : myParent.value.localRoot.value;
    const matchingRoutes = [];
    for (const route of routesArray) {
      if (!route.path)
        throw new Error("route does not have a path to compare against");
      const matches = new URLPattern(
        joinPathParts(joinPathParts(baseConfig.get("ROOT_URL"), parentPath), route.path)
      ).exec(joinPathParts(parentPath, localPath.value), baseConfig.get("ROOT_URL"));
      if (matches)
        matchingRoutes.push({ route, matchInfo: matches });
    }
    const matchingRoute = assertExactlyOne(
      matchingRoutes,
      `Not exactly one route found for ${localPath.value} within parent ${myParent.value === IS_ROOT_ROUTER ? "root" : myParent.value.localRoot.value} but ${matchingRoutes.length}`
    )[0];
    routesArray.forEach((route) => {
      if (route !== matchingRoute.route && route.subRouter)
        route.subRouter.active.value = false;
    });
    if (!matchingRoute.route.subRouter?.active.value)
      matchingRoute.route.subRouter?.init(routerObject);
    matchingRoute.route.enter?.(variables.value);
    return matchingRoute;
  });
  const variables = computed(() => {
    const variables2 = {};
    for (const route of routesArray) {
      const matches = new URLPattern(joinPathParts(baseConfig.get("ROOT_URL"), route.path)).exec(
        localPath.value,
        baseConfig.get("ROOT_URL")
      );
      for (const variable of Object.keys(matches?.pathname.groups ?? {})) {
        if (variables2[variable] !== void 0)
          throw new Error("this should not happen");
        variables2[variable] = matches?.pathname.groups[variable] ?? "";
      }
    }
    const parentVariables = myParent.value === IS_ROOT_ROUTER ? {} : myParent.value?.variables.value ?? {};
    const duplicateVariableNames = Object.keys(parentVariables).filter((variableName) => isNaN(+variableName)).filter((variableName) => Object.hasOwn(variables2, variableName));
    if (duplicateVariableNames.length)
      throw new Error(
        `Cannot define the same variable name in one route multiple times (${duplicateVariableNames.join(", ")})`
      );
    return { ...parentVariables, ...variables2 };
  });
  const subroute = computed(() => currentRoute.value.matchInfo.pathname.groups[0] ?? "");
  const myroute = computed(() => currentRoute.value.matchInfo.pathname.input.replace(subroute.value ?? "", ""));
  const generateRoutingState = (name, options) => {
    if (!myParent.value)
      throw new Error("router not initialized");
    const route = name ? routesArray.find((r) => r.name === name) : currentRoute.value?.route;
    if (!route)
      throw new Error("this should not happen");
    if (!route.path)
      throw new Error("route does not have a path to go to");
    let localURL = fillPathWithVariables(route.path, { ...variables.value, ...options?.params });
    return generateFullRoutingStateFromInput({
      path: joinPathParts(myParent.value === IS_ROOT_ROUTER ? "/" : myParent.value.localRoot.value, localURL),
      searchParams: options?.searchParams,
      hash: options?.hash
    });
  };
  const routerObject = {
    init: (parent) => {
      if (routerObject.active.value)
        throw new Error("router already initialized");
      if (!parent)
        myParent.value = IS_ROOT_ROUTER;
      else
        myParent.value = parent;
      routerObject.active.value = true;
      return routerObject;
    },
    active: new Signal(false),
    routes,
    routerName: config?.routerName,
    localPath,
    currentRoute,
    variables,
    subroute,
    localRoot: myroute,
    outlet: computed(() => {
      if (!routerObject.active.value)
        return;
      try {
        if (!currentRoute.value.route.render && currentRoute.value.route.subRouter)
          return currentRoute.value.route.subRouter.outlet.value;
        return currentRoute.value.route.render?.(variables.value);
      } catch (err) {
        logger.warn({ err }, "Error running outlet compute in make-router");
        return config?.fallback?.(variables.value) ?? ` 404 `;
      }
    }),
    goTo: (name, options) => {
      return setRoutingState({
        ...options,
        ...generateRoutingState(name, options)
      });
    },
    link: (name, options) => {
      return generateHrefFromRoutingState(generateRoutingState(name, options));
    },
    linkPath(rsi) {
      if (!myParent.value)
        throw new Error("router not initialized");
      return generateHrefFromRoutingState(
        generateFullRoutingStateFromInput({
          path: joinPathParts(myParent.value === IS_ROOT_ROUTER ? "/" : myParent.value.localRoot.value, rsi.path),
          searchParams: rsi.searchParams,
          hash: rsi.hash
        })
      );
    },
    /**
     * go hierarchically up the routing tree or do a browser back, depending on the given mode
     * - defaults to `history`
     *
     * no matter what mode was passed, if no browser history exists we will go up hierarchically instead
     */
    goBack: (mode) => {
      if (!myParent.value)
        throw new Error("router not initialized");
      if (mode === "history" && window.history.length > 1) {
        window.history.back();
      } else {
        const path = localPath.value === "" || localPath.value === "/" ? (myParent.value === IS_ROOT_ROUTER ? routerObject : myParent.value).linkPath({ path: "/" }) : routerObject?.linkPath({ path: "/" });
        return setRoutingState({ path });
      }
    }
  };
  return routerObject;
}
export {
  makeRouter
};
